import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { IRootReducerState } from "../../../store/IRootReducer";
import { logger } from "../../../logger-init";

/** data-testid of the prompt's dialog, so it can tell itself apart from other dialogs. */
export const PRODUCTION_PROMPT_TEST_ID = "non-production-warning";

interface PromptInstance {
  id: string;
  displayname: string;
  isProduction?: boolean;
}

// "Stay Here" lasts for the browser session, per user and per instance.
const stayedKey = (email: string, instanceId: string) =>
  `skysupport.prodSwitchPrompt.stayed:${email.toLowerCase()}:${instanceId}`;

function readStayed(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeStayed(key: string): void {
  try {
    sessionStorage.setItem(key, "1");
  } catch (error) {
    logger.warn("Could not remember the Stay Here choice", { error: String(error) });
  }
}

// SkyDialog stacks below SkyDrawer (z 1050 vs 10001+), and a Radix layer opened
// later takes the pointer: while a drawer is open the prompt can't be clicked,
// and a click aimed at "Stay Here" lands on the drawer's backdrop and closes it
// (dropping e.g. ?user= from the URL). So the prompt waits until no other
// dialog is open.
function anotherDialogOpen(): boolean {
  return (
    document.querySelector(
      `[role="dialog"][data-state="open"]:not([data-testid="${PRODUCTION_PROMPT_TEST_ID}"])`,
    ) !== null
  );
}

function useAnotherDialogOpen(enabled: boolean): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    const update = () => setOpen(anotherDialogOpen());
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state"],
    });
    return () => observer.disconnect();
  }, [enabled]);
  return enabled && open;
}

/**
 * The Sandbox→Production prompt. "Production" is a production instance of the
 * tenant you're in (/admin/instances) that you can actually reach — it is in
 * your own tenant→instance list (/admin/instances/my, the tenant/instance
 * switcher's data). resolve-instance marks every instance Admin or Manager
 * isPlatformAdmin/isTenantAdmin, so those flags say nothing about Production
 * (#1262).
 */
export function useProductionSwitchPrompt({
  selectedInstanceId,
  email,
}: {
  selectedInstanceId: string;
  email: string | undefined;
}) {
  const instances = useSelector(
    (state: IRootReducerState) =>
      (state.instancesReducer?.instancesData?.instances ?? []) as PromptInstance[],
  );
  const myTenants = useSelector(
    (state: IRootReducerState) =>
      (state.tenantsReducer?.tenantsData?.tenants ?? []) as Array<{
        instances?: Array<{ id: string }>;
      }>,
  );
  const permissions = useSelector((state: IRootReducerState) => state.authReducer?.permissions);
  const resolvedRole = useSelector((state: IRootReducerState) => state.authReducer?.resolvedRole);

  const isAdminOrManager =
    permissions?.isPlatformAdmin ||
    permissions?.isTenantAdmin ||
    resolvedRole === "skysupport_admin" ||
    resolvedRole === "skysupport_manager";

  const reachable = new Set(myTenants.flatMap((t) => (t.instances ?? []).map((i) => i.id)));
  const selectedInstance = instances.find((i) => i.id === selectedInstanceId);
  const productionInstance =
    selectedInstance && selectedInstance.isProduction === false
      ? instances.find((i) => i.isProduction === true && reachable.has(i.id))
      : undefined;

  const key = email && selectedInstanceId ? stayedKey(email, selectedInstanceId) : "";
  const [stayedKeys, setStayedKeys] = useState<ReadonlySet<string>>(() => new Set());
  const stayed = !key || stayedKeys.has(key) || readStayed(key);

  const eligible = isAdminOrManager && !!productionInstance && !stayed;
  const blocked = useAnotherDialogOpen(eligible);

  const stayHere = useCallback(() => {
    if (!key) return;
    writeStayed(key);
    setStayedKeys((prev) => new Set(prev).add(key));
  }, [key]);

  return {
    show: eligible && !blocked,
    selectedInstance,
    productionInstance,
    stayHere,
  };
}
